import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { toast } from '../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';

type BulkUploadModalProps = {
  inviteEmployeesFromCsv: (csvFile: File) => void;
};

export const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  inviteEmployeesFromCsv,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const EM = ADMIN_PAGES_KEYS.employeeManagement;
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!uploadedFile) {
      const files = event.target.files;
      const splitFileString = files && files[0]?.name.split('.');
      if (
        files &&
        splitFileString &&
        splitFileString[splitFileString.length - 1]?.toLowerCase() === 'csv'
      ) {
        setUploadedFile(files[0]);
      } else {
        toast.error(t(EM.bulkUpload.invalidFileType), {
          position: 'bottom-center',
        });
      }
    }
  };

  const handleDownload = () => {
    const fileUrl = process.env.PUBLIC_URL + '/files/employee_bulk_sample.csv';
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = 'employee_bulk_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
      {uploadedFile ? (
        <Paper
          variant='outlined'
          sx={{
            p: 3,
            borderRadius: '6px',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'action.hover',
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: '#093725' }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
              {uploadedFile.name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
              {(uploadedFile.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>

          <Stack direction='row' spacing={1.5} sx={{ mt: 1 }}>
            <Button
              variant='outlined'
              size='small'
              color='error'
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => setUploadedFile(undefined)}
              sx={{ borderRadius: '6px', textTransform: 'none', fontWeight: 600 }}
            >
              Remove
            </Button>
            <Button
              variant='contained'
              size='small'
              onClick={() => inviteEmployeesFromCsv(uploadedFile)}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#093725',
                '&:hover': { bgcolor: '#062619' },
              }}
            >
              {t(EM.bulkUpload.bulkInviteFromCsv)}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <>
          <Paper
            variant='outlined'
            component='label'
            htmlFor='upload-file-input'
            sx={{
              p: 4,
              borderRadius: '6px',
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              cursor: 'pointer',
              bgcolor: 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#093725',
                bgcolor: 'rgba(9, 55, 37, 0.02)',
              },
            }}
          >
            <input
              type='file'
              accept='.csv'
              onChange={addFileViaInput}
              id='upload-file-input'
              style={{ display: 'none' }}
            />
            <UploadFileRoundedIcon sx={{ fontSize: 40, color: '#093725' }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                {t(EM.bulkUpload.uploadFile)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                Click to browse or drag and drop your CSV file
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant='outlined'
              startIcon={<DownloadRoundedIcon />}
              onClick={handleDownload}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'action.active',
                  bgcolor: 'action.hover',
                },
              }}
            >
              {t(EM.bulkUpload.downloadSampleCsv)}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
